import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { getSearches, pushSearch, clearSearches } from "../lib/recent";
import { Img, inr } from "./ui-bits";
import { useI18n } from "../context/I18nContext";
import { Search, Clock, Tag, X } from "lucide-react";

/** Header search with live suggestions, recent searches and full keyboard support. */
export default function SearchBar({ className = "", autoFocus = false, onDone }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ products: [], categories: [] });
  const [recent, setRecent] = useState([]);
  const [active, setActive] = useState(-1);
  const box = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    const my = ++seq.current;
    if (term.length < 2) { setData({ products: [], categories: [] }); return undefined; }
    const t = setTimeout(() => {
      api.get("/catalog/suggest", { params: { q: term }, silent: true })
        .then(r => { if (my === seq.current) { setData(r.data); setActive(-1); } }).catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const go = (path) => { setOpen(false); onDone?.(); nav(path); };
  const submit = (term) => {
    const t = (term ?? q).trim(); if (!t) return;
    pushSearch(t); setQ(t); go(`/marketplace?q=${encodeURIComponent(t)}`);
  };

  const rows = [
    ...data.products.map(p => ({ kind: "product", p })),
    ...data.categories.map(c => ({ kind: "category", c })),
    ...(q.trim().length >= 2 ? [{ kind: "all" }] : recent.map(r => ({ kind: "recent", r }))),
  ];
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, -1)); }
    else if (e.key === "Escape") setOpen(false);
    else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (!row || row.kind === "all") return submit();
      if (row.kind === "product") { pushSearch(row.p.name); go(`/product/${row.p.id}`); }
      else if (row.kind === "category") go(`/marketplace?category=${row.c}`);
      else submit(row.r);
    }
  };

  const show = open && (rows.length > 0);
  return (
    <div ref={box} className={`relative ${className}`} role="search">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => { setRecent(getSearches()); setOpen(true); }} onKeyDown={onKey}
               autoFocus={autoFocus} data-testid="global-search" role="combobox" aria-autocomplete="list" aria-controls="search-suggest-list" aria-label="Search products" aria-expanded={show} autoComplete="off"
               placeholder={t("search_ph")} maxLength={80}
               className="w-full h-10 pl-10 pr-9 rounded-full bg-muted/70 border border-transparent text-sm placeholder:text-muted-foreground focus:bg-background focus:border-primary/40 focus:ring-2 focus:ring-ring/30 outline-none transition" />
        {q && <button type="button" aria-label="Clear search" onClick={() => { setQ(""); setData({ products: [], categories: [] }); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
      </form>
      {show && (
        <div className="absolute left-0 right-0 top-12 z-50 bg-popover border rounded-xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto" id="search-suggest-list" role="listbox" aria-label="Search suggestions" data-testid="search-suggestions">
          {rows.map((row, i) => {
            const cls = `w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm ${active === i ? "bg-accent" : "hover:bg-accent/60"}`;
            if (row.kind === "product") return (
              <button key={row.p.id} role="option" aria-selected={active === i} className={cls} onMouseEnter={() => setActive(i)} onClick={() => { pushSearch(row.p.name); go(`/product/${row.p.id}`); }}>
                <Img src={row.p.image} alt="" className="w-10 h-10 rounded-md object-cover bg-muted shrink-0" />
                <span className="flex-1 min-w-0"><span className="block truncate font-medium">{row.p.name}</span><span className="text-xs text-muted-foreground capitalize">{row.p.category}</span></span>
                <span className="text-primary font-semibold">{inr(row.p.price)}</span>
              </button>);
            if (row.kind === "category") return (
              <button key={`c-${row.c}`} role="option" aria-selected={active === i} className={cls} onMouseEnter={() => setActive(i)} onClick={() => go(`/marketplace?category=${row.c}`)}>
                <Tag className="w-4 h-4 text-secondary" /><span>Browse <strong className="capitalize">{row.c}</strong></span>
              </button>);
            if (row.kind === "all") return (
              <button key="all" role="option" aria-selected={active === i} className={`${cls} border-t text-primary`} onMouseEnter={() => setActive(i)} onClick={() => submit()}>
                <Search className="w-4 h-4" /><span>See all results for <strong>"{q.trim()}"</strong></span>
              </button>);
            return (
              <button key={`r-${row.r}`} role="option" aria-selected={active === i} className={cls} onMouseEnter={() => setActive(i)} onClick={() => submit(row.r)}>
                <Clock className="w-4 h-4 text-muted-foreground" /><span className="flex-1 truncate">{row.r}</span>
                {i === data.products.length + data.categories.length && <span role="button" tabIndex={0} className="text-[11px] text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); clearSearches(); setRecent([]); }}>Clear</span>}
              </button>);
          })}
        </div>
      )}
    </div>
  );
}
